
import React, { useState } from 'react';
import Button from '../../../components/base/Button';

const HeroSection: React.FC = () => {
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d42esc9ha0trr1ha99hg', {
        method: 'POST',
        body: formDataToSend,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', phone: '', message: '' });
        setTimeout(() => {
          setShowBookingForm(false);
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center bg-white pt-24">
      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 text-center">
        <div className="space-y-8">
          <h2 className="text-5xl md:text-7xl font-light text-black leading-tight tracking-tight">
            Intelligence that
            <br />
            <span className="font-medium">works for you</span>
          </h2>
          
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
            Deploy autonomous AI systems that transform your business operations, 
            automate lead generation, and drive sustainable growth.
          </p>
          
          <div className="pt-8 space-y-4">
            <Button 
              variant="primary" 
              size="lg"
              onClick={() => setShowBookingForm(true)}
              className="text-lg px-12 py-4"
            >
              Get Started
            </Button>
            <div className="text-sm text-gray-500">
              Free consultation • No commitment required
            </div>
          </div>
        </div>
      </div>

      {/* Booking Form Modal */}
      {showBookingForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-2xl font-medium text-black">Get Started</h3>
              <button
                onClick={() => setShowBookingForm(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-black cursor-pointer rounded-full hover:bg-gray-100 transition-colors"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} data-readdy-form id="strategy-call-form">
              <div className="space-y-4">
                <div>
                  <input
                    type="text"
                    name="name"
                    placeholder="Full Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-4 border border-gray-200 rounded-2xl focus:outline-none focus:border-black transition-colors text-base bg-gray-50 focus:bg-white"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-4 border border-gray-200 rounded-2xl focus:outline-none focus:border-black transition-colors text-base bg-gray-50 focus:bg-white"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    name="company"
                    placeholder="Company Name"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-4 border border-gray-200 rounded-2xl focus:outline-none focus:border-black transition-colors text-base bg-gray-50 focus:bg-white"
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-4 border border-gray-200 rounded-2xl focus:outline-none focus:border-black transition-colors text-base bg-gray-50 focus:bg-white"
                  />
                </div>
                <div>
                  <textarea
                    name="message"
                    placeholder="Tell us about your business needs..."
                    value={formData.message}
                    onChange={handleInputChange}
                    maxLength={500}
                    rows={3}
                    className="w-full px-4 py-4 border border-gray-200 rounded-2xl focus:outline-none focus:border-black transition-colors text-base resize-none bg-gray-50 focus:bg-white"
                  ></textarea>
                  <div className="text-xs text-gray-400 mt-2">
                    {formData.message.length}/500 characters
                  </div>
                </div>
              </div>
              
              <div className="mt-8">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full text-base"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </Button>
              </div>
              
              {submitStatus === 'success' && (
                <div className="mt-4 p-4 bg-green-50 text-green-800 rounded-2xl text-sm text-center">
                  Request submitted successfully! We'll contact you soon.
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-50 text-red-800 rounded-2xl text-sm text-center">
                  Something went wrong. Please try again.
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default HeroSection;
